import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import ShowArchiveCard, { ShowArchiveCardList, cardControl, joinLocation } from './ShowArchiveCard';
import RelationshipCard, { RelationshipCardList, capacityLabel, relCardText } from './RelationshipCard';
import ArtistMembersTab from './ArtistMembersTab';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';
import DeletionRequestModal from './DeletionRequestModal';
import ActionsDropdown from './ActionsDropdown';
import MobileTabSelect from './MobileTabSelect';
import FlyerHistoryTabs, { S as FS, Smobile as FSmobile, FLYER_PAGE_SIZE } from './FlyerHistoryTabs';
import { VISUAL_ARTIST_TYPE } from '../artistStyleUtils';
import { ZoomHint, ImageLightbox, ImageQuickEditModal } from './ZoomableImage';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// (2026-10-12) Photos moved from the top folder to the bottom folder for
// non-visual artists, with Event Photos / Non-Event Photos sub-tabs.
// (2026-10-15) Visual Artists followed: Photos now sits in the bottom folder
// for every artist type (see VA_PARENT_TABS).
const INFO_TABS = ['Show Stats', 'Bio', 'Links', 'Projects', 'Additional Info'];
const PAGE_SIZE = 25;

// ── Parent tabs for Section 3 ─────────────────────────────────────────────────
const PARENT_TABS = ['Show Archive', 'Shows by Year', 'Members', 'Relationships', 'Locations', 'Flyers', 'Photos'];
const PHOTOS_SUBTABS = ['Event Photos', 'Non-Event Photos'];
const RELATIONSHIPS_SUBTABS = ['Artist Relationships', 'Promoter Relationships'];
const LOCATIONS_SUBTABS = ['Shows Map', 'Geographic Breakdown', 'Festivals Played', 'Venues Played'];

// ── Visual Artist variant (2026-10-12, SHARC_Spec_Visual_Artists_Image_Credits §5.3)
// Shown when Artist Type = "Visual Artist"; every count comes from the images
// credited to the artist (GET /artists/{id}/credits — getArtistCredits.js).
// (2026-10-15) Photos moved from the top folder to the bottom folder. The
// former Artist Photos / Photos of Artists tab was folded into Photos, which
// now has three sub-tabs (VA_PHOTOS_SUBTABS, see VAPhotosTab).
const VA_INFO_TABS = ['Show Stats', 'Bio', 'Links', 'Additional Info'];
const VA_PARENT_TABS = ['Show Archive', 'Shows by Year', 'Relationships', 'Locations', 'Flyers', 'Photos'];
const VA_PHOTOS_SUBTABS = ['Event Photo Credits', 'Non-Event Photo Credits', 'Non-Event Photos'];
const VA_LOCATIONS_SUBTABS = ['Shows Map', 'Geographic Breakdown', 'Festivals', 'Venues'];
// (2026-10-22) A Visual Artist credited on a Flyer – Multiple image is related
// to the image's primary record (artist / promoter / festival / venue),
// counted in images — the "Multi-Event Flyers" column. Festival and venue
// primaries get their own sub-tabs (getArtistCredits festival_relationships /
// venue_relationships).
const VA_RELATIONSHIPS_SUBTABS = ['Artist Relationships', 'Promoter Relationships', 'Festival Relationships', 'Venue Relationships'];

// ── Pagination ────────────────────────────────────────────────────────────────
// Jump-to-page control — copied verbatim from PromoterDetail.js (2026-10-10)
// so Artist / Venue / Promoter / Festival detail pages paginate identically.
// A native <select> gives the scrollable page list and click-to-jump for free.
function Pagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={styles.pageJumpWrap}>
            <span style={styles.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={styles.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={styles.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Projects Tab (shared pattern) ─────────────────────────────────────────────
function ProjectsTab({ projects, entityType }) {
    if (!projects || projects.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No projects yet.</div>
                <a href="/add-project" style={{ color: '#bb86fc', fontSize: '13px', textDecoration: 'none' }}>+ Start a Project</a>
            </div>
        );
    }
    return (
        <div>
            {projects.map(p => (
                <div key={p.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a2a2a' }}>
                    <div style={{ marginBottom: '6px' }}>
                        <a href={`/projects/${p.id}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: '600', fontSize: '14px' }}>{p.name}</a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1, height: '8px', backgroundColor: '#2c2c2c', borderRadius: '4px', overflow: 'hidden', maxWidth: '200px' }}>
                            <div style={{ width: `${Math.min(p.pct_complete, 100)}%`, height: '100%', backgroundColor: '#bb86fc', borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#bb86fc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {p.pct_complete > 100 ? '100+%' : `${p.pct_complete}%`}
                        </span>
                        <span style={{ fontSize: '12px', color: '#555' }}>{p.actual_value} / {p.estimated_value} shows</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>{p.metric} · {p.date_start} to {p.date_end}</div>
                </div>
            ))}
        </div>
    );
}

// ── Sub-tab bar ───────────────────────────────────────────────────────────────
// Mobile (2026-10-28): a single dropdown instead of a scrolling button row.
function SubTabBar({ tabs, active, onChange }) {
    const isMobile = useIsMobile();
    if (isMobile) return <MobileTabSelect variant="sub" tabs={tabs} active={active} onChange={onChange} />;
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Photos Tab ────────────────────────────────────────────────────────────────
// split (2026-10-12, bottom-folder Photos for non-visual artists): shows
// Event Photos (Event Photo – Artist images picturing this artist) and
// Non-Event Photos (images assigned to the artist record) as sub-tabs.
// Add Photo always uploads a Non-Event photo for this artist; event photos
// are added from an event's Images tab (Event Photo – Artist).
// Unsplit (Visual Artist, 2026-10-15): no inner sub-tabs, Non-Event Photos only —
// a Visual Artist can't be on a lineup, so it is never pictured in an event
// photo; the photos it is credited for live in the other Photos sub-tabs.
// eventSummary(eventId) (2026-10-19): "<Month> <Year>: <City>, <State>" for an
// event photo's link, built from the page's show_history (the pictured artist
// is always on that event's lineup). Falls back to "View Show →".
function PhotosTab({ artistId, artistName, defaultImageId, onDefaultChanged, split = false, onCount, eventSummary }) {
    const [subTab, setSubTab] = useState(0);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [hoveredThumb, setHoveredThumb] = useState(null);
    const [editingImage, setEditingImage] = useState(null);
    const [showUpload, setShowUpload] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!artistId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'artist', entityId: artistId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [artistId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    // Visual Artist Photos sub-tab label count (2026-10-15): reports the
    // Non-Event Photos count, kept live across upload / delete.
    useEffect(() => {
        if (onCount && !loading) onCount(images.filter(img => img.image_kind !== 'event_photo_artist').length);
    }, [images, loading, onCount]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
        if (split) setSubTab(1); // new uploads are Non-Event photos
        // Backend may have auto-set this upload as the artist's default —
        // sync that up to the parent immediately rather than waiting on a
        // refetch (createImage.js reports this via becameDefault).
        if (img.becameDefault) {
            onDefaultChanged(img.id, img.image_url);
        }
    };

    const [removingDefault, setRemovingDefault] = useState(false);

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'artist', entityId: artistId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'artist', entityId: artistId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    const handleImageSaved = (updated) => {
        setImages(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i));
        setLightbox(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
    };

    const handleImageDeleted = (id) => {
        setImages(prev => prev.filter(i => i.id !== id));
        setLightbox(null); // image no longer exists — close the lightbox behind it
    };

    if (loading) return <div style={styles.emptyTab}>Loading photos…</div>;
    if (error) return <div style={styles.emptyTab}>{error}</div>;

    const isEventPhoto = img => img.image_kind === 'event_photo_artist';
    const eventPhotos = images.filter(isEventPhoto);
    const nonEventPhotos = images.filter(img => !isEventPhoto(img));
    const showingEvent = split && subTab === 0;
    const shown = split && showingEvent ? eventPhotos : nonEventPhotos;
    const emptyText = !split
        ? 'No photos yet for this artist.'
        : showingEvent
            ? "No event photos yet. Add one from an event's Images tab (Event Photo – Artist)."
            : 'No non-event photos yet for this artist.';

    return (
        <div>
            {split && (
                <SubTabBar
                    tabs={[`${PHOTOS_SUBTABS[0]} (${eventPhotos.length})`, `${PHOTOS_SUBTABS[1]} (${nonEventPhotos.length})`]}
                    active={subTab}
                    onChange={setSubTab}
                />
            )}
            {split && <div style={{ height: '16px' }} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {shown.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>{emptyText}</div>
                </div>
            ) : (
                <div style={{ ...styles.photoGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                    {shown.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)} onMouseEnter={() => setHoveredThumb(img.id)} onMouseLeave={() => setHoveredThumb(null)}>
                                    <img src={img.image_url} alt={img.caption || artistName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                    <ZoomHint visible={hoveredThumb === img.id} />
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {isEventPhoto(img) && img.entity_id && (
                                        <Link to={`/events/${img.entity_id}`} style={{ display: 'block', fontSize: '11px', color: '#bb86fc', textDecoration: 'none', marginTop: '4px' }}>{(eventSummary && eventSummary(img.entity_id)) || 'View Show →'}</Link>
                                    )}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <ImageLightbox
                    src={lightbox.image_url}
                    alt={lightbox.caption || ''}
                    caption={lightbox.caption}
                    viewShowTo={isEventPhoto(lightbox) && lightbox.entity_id ? `/events/${lightbox.entity_id}` : undefined}
                    onClose={() => setLightbox(null)}
                    onEdit={() => setEditingImage(lightbox)}
                    imageMeta={lightbox}
                />
            )}

            {editingImage && (
                <ImageQuickEditModal
                    image={editingImage}
                    onClose={() => setEditingImage(null)}
                    onSaved={handleImageSaved}
                    onDeleted={handleImageDeleted}
                />
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="artist"
                    presetEntityId={artistId}
                    presetEntityName={artistName}
                />
            )}
        </div>
    );
}

// ── Show History Tab ──────────────────────────────────────────────────────────
// creditColumn (Visual Artist, 2026-10-12): adds a Credit column showing how
// the artist is credited on each show (Flyer / Multi-Event Flyer / Photo – X).
function ShowHistoryTab({ shows, attendedMap, performedMap, workedMap, onToggleAttended, togglingMap, creditColumn = false, emptyText = 'No shows recorded yet.' }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>{emptyText}</div>;
    const q = normalizeStr(search);
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, show.city, show.state, show.country, show.venue_name, show.festival_name, show.other_artists, creditColumn ? show.credit_label : null].some(val => val && normalizeStr(val).includes(q));
    }) : shows;
    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by date, city, state, country, venue, artists..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No shows match your search.</div> : (
                <>
                    {isMobile ? (
                        <ShowArchiveCardList>
                            {paginated.map(show => {
                                const showNum = totalShows - shows.indexOf(show);
                                const isAttended = attendedMap[show.event_id] === true;
                                const isPerformed = performedMap[show.event_id] === true;
                                const isWorked = workedMap[show.event_id] === true;
                                const isToggling = togglingMap[show.event_id];
                                return (
                                    <ShowArchiveCard
                                        key={show.event_id}
                                        title={`Show #${showNum}`}
                                        titleTo={`/events/${show.event_slug || show.event_id}`}
                                        date={show.event_date}
                                        venueName={show.venue_name}
                                        venueTo={show.venue_id ? `/venues/${show.venue_slug || show.venue_id}` : null}
                                        location={joinLocation(show.city, show.state, show.country)}
                                        festivalName={show.festival_name}
                                        festivalTo={show.festival_slug ? `/festivals/${show.festival_slug}` : null}
                                        lineup={show.other_artists}
                                        lineupLabel={creditColumn ? null : 'With'}
                                        extraLines={creditColumn && show.credit_label ? [<><span style={{ color: '#aaa' }}>Credit: </span><span style={{ color: '#bb86fc' }}>{show.credit_label}</span></>] : null}
                                        footer={isPerformed ? <span style={{ ...styles.performedBadge, ...cardControl }}>Performed</span>
                                        : isWorked ? <span style={{ ...styles.workedBadge, ...cardControl }}>Worked</span>
                                        : <button onClick={() => onToggleAttended(show.event_id)} disabled={isToggling} style={{ ...(isAttended ? styles.attendedBtnOn : styles.attendedBtnOff), ...cardControl }}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                    />
                                );
                            })}
                        </ShowArchiveCardList>
                    ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>{creditColumn ? 'Lineup' : 'Additional Artists'}</th>
                                    {creditColumn && <th style={styles.th}>Credit</th>}
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isPerformed = performedMap[show.event_id] === true;
                                    const isWorked = workedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link> : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.other_artists ? ` - ${show.other_artists}` : ''}</>
                                                    : show.other_artists || '—'}
                                            </td>
                                            {creditColumn && <td style={{ ...styles.td, color: '#bb86fc', fontSize: '13px' }}>{show.credit_label || '—'}</td>}
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? <span style={styles.performedBadge}>Performed</span>
                                                    : isWorked ? <span style={styles.workedBadge}>Worked</span>
                                                    : <button onClick={() => onToggleAttended(show.event_id)} disabled={isToggling} style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;
    const years = shows.map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const gridLines = [];
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);
    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;
    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>; })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />; })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>)}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Festivals Played Tab ──────────────────────────────────────────────────────
function FestivalsPlayedTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    const festivalShows = shows ? shows.filter(s => s.festival_id) : [];
    if (festivalShows.length === 0) return <div style={styles.emptyTab}>No festival shows recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? festivalShows.filter(show => {
        const dateStr = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, show.festival_name, show.city, show.state, show.country, show.venue_name, show.other_artists].some(val => val && normalizeStr(val).includes(q));
    }) : festivalShows;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by date, festival, city, venue, artists..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No festival shows match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(show => (
                                <RelationshipCard
                                    key={show.event_id}
                                    title={show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—'}
                                    titleTo={`/events/${show.event_slug || show.event_id}`}
                                    lines={[
                                        (show.festival_name || show.venue_name) && (
                                            <>
                                                {show.festival_slug
                                                    ? <Link to={`/festivals/${show.festival_slug}`} style={relCardText.link}>{show.festival_name}</Link>
                                                    : <span style={relCardText.strong}>{show.festival_name}</span>}
                                                {show.festival_name && show.venue_name && <span style={relCardText.muted}> at </span>}
                                                {show.venue_name && (show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={relCardText.link}>{show.venue_name}</Link>
                                                    : <span style={relCardText.strong}>{show.venue_name}</span>)}
                                            </>
                                        ),
                                        joinLocation(show.city, show.state, show.country),
                                        show.other_artists && (
                                            <>
                                                <span style={relCardText.lineupLabel}>With </span>
                                                <span style={relCardText.lineup}>{show.other_artists}</span>
                                            </>
                                        ),
                                    ]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Festival</th><th style={styles.th}>City</th><th style={styles.th}>State</th><th style={styles.th}>Country</th><th style={styles.th}>Venue</th><th style={styles.th}>Additional Artists</th></tr></thead>
                                <tbody>
                                    {paginated.map((show, idx) => {
                                        const formattedDate = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
                                        return (
                                            <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                                <td style={styles.td}><Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{formattedDate}</Link></td>
                                                <td style={styles.td}>{show.festival_slug ? <Link to={`/festivals/${show.festival_slug}`} style={styles.tableLink}>{show.festival_name}</Link> : show.festival_name || '—'}</td>
                                                <td style={styles.td}>{show.city || '—'}</td>
                                                <td style={styles.td}>{show.state || '—'}</td>
                                                <td style={styles.td}>{show.country || '—'}</td>
                                                <td style={styles.td}>{show.venue_id ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link> : show.venue_name || '—'}</td>
                                                <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{show.other_artists || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Venues Played Tab ─────────────────────────────────────────────────────────
function VenuesPlayedTab({ venues }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    if (!venues || venues.length === 0) return <div style={styles.emptyTab}>No venues recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? venues.filter(v => [v.name, v.city, v.state, v.country, v.capacity != null ? String(v.capacity) : ''].some(val => val && normalizeStr(val).includes(q))) : venues;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by venue, city, state, country, capacity..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No venues match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(venue => (
                                <RelationshipCard
                                    key={venue.id}
                                    title={venue.name}
                                    titleTo={`/venues/${venue.slug || venue.id}`}
                                    titleMeta={capacityLabel(venue.capacity)}
                                    lines={[joinLocation(venue.city, venue.state, venue.country)]}
                                    counts={[{ value: venue.times_played, label: 'Shows' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead><tr><th style={styles.th}>Venue</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Capacity</th><th style={styles.th}>Shows</th></tr></thead>
                                <tbody>
                                    {paginated.map((venue, idx) => (
                                        <tr key={venue.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link></td>
                                            <td style={styles.td}>{venue.city || '—'}</td>
                                            <td style={styles.td}>{venue.state || '—'}</td>
                                            <td style={styles.td}>{venue.country || '—'}</td>
                                            <td style={styles.td}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</td>
                                            <td style={styles.td}><span style={styles.showCount}>{venue.times_played}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Artist Relationships Tab ──────────────────────────────────────────────────
function ArtistRelationshipsTab({ playedWith, showFlyerCount = false }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    if (!playedWith || playedWith.length === 0) return <div style={styles.emptyTab}>No co-billed artists recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? playedWith.filter(pw => [pw.name, pw.city, pw.state, pw.country].some(val => val && normalizeStr(val).includes(q))) : playedWith;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by artist, city, state, country..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No artists match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(pw => (
                                <RelationshipCard
                                    key={pw.id}
                                    title={pw.name}
                                    titleTo={`/artists/${pw.slug || pw.id}`}
                                    lines={[joinLocation(pw.city, pw.state, pw.country)]}
                                    counts={[
                                        { value: showFlyerCount && !pw.times_played_together ? '—' : `${pw.times_played_together}x`, label: 'Shows Together' },
                                        showFlyerCount && { value: pw.multi_event_images ? `${pw.multi_event_images}x` : '—', label: 'Multi-Event Flyers' },
                                    ]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead><tr><th style={styles.th}>Artist</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Shows Together</th>{showFlyerCount && <th style={styles.th}>Multi-Event Flyers</th>}</tr></thead>
                                <tbody>
                                    {paginated.map((pw, idx) => (
                                        <tr key={pw.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/artists/${pw.slug || pw.id}`} style={styles.tableLink}>{pw.name}</Link></td>
                                            <td style={styles.td}>{pw.city || '—'}</td>
                                            <td style={styles.td}>{pw.state || '—'}</td>
                                            <td style={styles.td}>{pw.country || '—'}</td>
                                            <td style={styles.td}>{showFlyerCount && !pw.times_played_together ? '—' : <span style={styles.showCount}>{pw.times_played_together}x</span>}</td>
                                            {showFlyerCount && <td style={styles.td}>{pw.multi_event_images ? <span style={styles.showCount}>{pw.multi_event_images}x</span> : '—'}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Promoter Relationships Tab ────────────────────────────────────────────────
function PromoterRelationshipsTab({ promoterRelationships, showFlyerCount = false }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    if (!promoterRelationships || promoterRelationships.length === 0) return <div style={styles.emptyTab}>No promoter relationships recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? promoterRelationships.filter(p => [p.name, p.city, p.state, p.country].some(val => val && normalizeStr(val).includes(q))) : promoterRelationships;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by promoter, city, state, country..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No promoters match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(p => (
                                <RelationshipCard
                                    key={p.id}
                                    title={p.name}
                                    titleTo={`/promoters/${p.slug || p.id}`}
                                    lines={[joinLocation(p.city, p.state, p.country)]}
                                    counts={[
                                        { value: showFlyerCount && !p.times_promoted ? '—' : `${p.times_promoted}x`, label: 'Shows Together' },
                                        showFlyerCount && { value: p.multi_event_images ? `${p.multi_event_images}x` : '—', label: 'Multi-Event Flyers' },
                                    ]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead><tr><th style={styles.th}>Promoter</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Shows Together</th>{showFlyerCount && <th style={styles.th}>Multi-Event Flyers</th>}</tr></thead>
                                <tbody>
                                    {paginated.map((p, idx) => (
                                        <tr key={p.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/promoters/${p.slug || p.id}`} style={styles.tableLink}>{p.name}</Link></td>
                                            <td style={styles.td}>{p.city || '—'}</td>
                                            <td style={styles.td}>{p.state || '—'}</td>
                                            <td style={styles.td}>{p.country || '—'}</td>
                                            <td style={styles.td}>{showFlyerCount && !p.times_promoted ? '—' : <span style={styles.showCount}>{p.times_promoted}x</span>}</td>
                                            {showFlyerCount && <td style={styles.td}>{p.multi_event_images ? <span style={styles.showCount}>{p.multi_event_images}x</span> : '—'}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Festival / Venue Relationships Tab (Visual Artist only, 2026-10-22) ──────
// Primary records of Flyer – Multiple images credited to the Visual Artist —
// same table / search / pagination as the Artist and Promoter tabs above.
function PrimaryRelationshipsTab({ rows, entityLabel, pathPrefix }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const lower = entityLabel.toLowerCase();
    if (!rows || rows.length === 0) return <div style={styles.emptyTab}>No {lower} relationships recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? rows.filter(r => [r.name, r.city, r.state, r.country].some(val => val && normalizeStr(val).includes(q))) : rows;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder={`Search by ${lower}, city, state, country...`} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No {lower}s match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>{entityLabel}</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Multi-Event Flyers</th></tr></thead>
                            <tbody>
                                {paginated.map((r, idx) => (
                                    <tr key={r.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`${pathPrefix}/${r.slug || r.id}`} style={styles.tableLink}>{r.name}</Link></td>
                                        <td style={styles.td}>{r.city || '—'}</td>
                                        <td style={styles.td}>{r.state || '—'}</td>
                                        <td style={styles.td}>{r.country || '—'}</td>
                                        <td style={styles.td}><span style={styles.showCount}>{r.multi_event_images}x</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Geographic Breakdown Tab (collapsible) ────────────────────────────────────
function GeographicBreakdownTab({ geography }) {
    const [collapsed, setCollapsed] = useState({});

    if (!geography || geography.length === 0) return <div style={styles.emptyTab}>No geographic data available.</div>;

    // Build rollup: country → state → city → venues
    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const count = parseInt(row.show_count) || 0;
        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;
        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;
        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;
        const venueKey = `${row.venue_id}__${row.venue_name || '—'}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: row.venue_id, name: row.venue_name || '—', slug: row.venue_slug, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const sortedCountries = Object.entries(rollup).sort((a, b) => b[1].total - a[1].total);

    const toggleKey = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    // All levels collapsed by default; user explicitly opens them
    const isOpen = (key) => {
        if (key in collapsed) return !collapsed[key];
        return false;
    };

    return (
        <div>
            {sortedCountries.map(([country, countryData]) => {
                const countryKey = `c:${country}`;
                const countryOpen = isOpen(countryKey);
                const sortedStates = Object.entries(countryData.states).sort((a, b) => b[1].total - a[1].total);

                return (
                    <div key={country} style={styles.geoGroup}>
                        {/* Country header */}
                        <div style={styles.geoCountryRow} onClick={() => toggleKey(countryKey)}>
                            <span style={styles.geoChevron}>{countryOpen ? '▾' : '▸'}</span>
                            <span style={styles.geoCountryName}>{country}</span>
                            <span style={styles.geoCount}>{countryData.total} show{countryData.total !== 1 ? 's' : ''}</span>
                        </div>

                        {countryOpen && sortedStates.map(([state, stateData]) => {
                            const stateKey = `s:${country}:${state}`;
                            const stateOpen = isOpen(stateKey);
                            const sortedCities = Object.entries(stateData.cities).sort((a, b) => b[1].total - a[1].total);

                            return (
                                <div key={state} style={styles.geoStateBlock}>
                                    {/* State header */}
                                    <div style={styles.geoStateRow} onClick={() => toggleKey(stateKey)}>
                                        <span style={styles.geoChevron}>{stateOpen ? '▾' : '▸'}</span>
                                        <span style={styles.geoStateName}>{state}</span>
                                        <span style={styles.geoCount}>{stateData.total} show{stateData.total !== 1 ? 's' : ''}</span>
                                    </div>

                                    {stateOpen && sortedCities.map(([city, cityData]) => {
                                        const cityKey = `ci:${country}:${state}:${city}`;
                                        const cityOpen = isOpen(cityKey);
                                        const sortedVenues = Object.values(cityData.venues).sort((a, b) => b.count - a.count);

                                        return (
                                            <div key={city} style={styles.geoCityBlock}>
                                                {/* City header */}
                                                <div style={styles.geoCityRow} onClick={() => toggleKey(cityKey)}>
                                                    <span style={styles.geoChevron}>{cityOpen ? '▾' : '▸'}</span>
                                                    <span style={styles.geoCityName}>{city}</span>
                                                    <span style={styles.geoCount}>{cityData.total} show{cityData.total !== 1 ? 's' : ''}</span>
                                                </div>

                                                {cityOpen && (
                                                    <div style={styles.geoVenueList}>
                                                        {sortedVenues.map(venue => (
                                                            <div key={venue.id || venue.name} style={styles.geoVenueRow}>
                                                                <span style={styles.geoVenueName}>
                                                                    {venue.id
                                                                        ? <Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link>
                                                                        : <span style={{ color: '#aaa' }}>{venue.name}</span>}
                                                                </span>
                                                                <span style={styles.showCount}>{venue.count}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

// ── Geographic Map Tab ────────────────────────────────────────────────────────
function GeographicMapTab({ showMap }) {
    const mapRef = React.useRef(null);
    const mapInstanceRef = React.useRef(null);
    const [leafletReady, setLeafletReady] = React.useState(!!window.L);
    React.useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        if (!document.getElementById('leaflet-css')) { const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link); }
        const script = document.createElement('script'); script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; script.onload = () => setLeafletReady(true); document.head.appendChild(script);
    }, []);
    React.useEffect(() => {
        if (!leafletReady || !mapRef.current || !showMap || showMap.length === 0) return;
        if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true });
        mapInstanceRef.current = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors', maxZoom: 18 }).addTo(map);
        const markerIcon = L.divIcon({ className: '', html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`, iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8] });
        const bounds = [];
        showMap.forEach(venue => {
            const lat = parseFloat(venue.lat); const lng = parseFloat(venue.lng);
            if (isNaN(lat) || isNaN(lng)) return;
            bounds.push([lat, lng]);
            const dates = venue.show_dates ? venue.show_dates.split(',').map(d => new Date(d.trim()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })) : [];
            const locationStr = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
            const showCount = parseInt(venue.show_count);
            const popupHtml = `<div style="font-family:sans-serif;min-width:180px;max-width:260px;"><div style="font-weight:bold;font-size:14px;margin-bottom:4px;"><a href="/venues/${venue.venue_slug || venue.venue_id}" style="color:#bb86fc;text-decoration:none;">${venue.venue_name} ↗</a></div>${locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${locationStr}</div>` : ''}<div style="font-size:12px;color:#aaa;margin-bottom:${dates.length ? '6px' : '0'};"><strong style="color:#e0e0e0;">${showCount}</strong> show${showCount !== 1 ? 's' : ''}</div>${dates.length > 0 ? `<div style="font-size:11px;color:#999;line-height:1.6;">${dates.slice(0, 10).join('<br/>')}${dates.length > 10 ? `<br/><em>+${dates.length - 10} more</em>` : ''}</div>` : ''}</div>`;
            L.marker([lat, lng], { icon: markerIcon }).bindPopup(popupHtml, { maxWidth: 280 }).addTo(map);
        });
        if (bounds.length === 1) map.setView(bounds[0], 12);
        else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
        return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
    }, [leafletReady, showMap]);
    if (!showMap || showMap.length === 0) return <div style={styles.emptyTab}>No geocoded venues available yet.</div>;
    return (
        <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>{showMap.length} venue{showMap.length !== 1 ? 's' : ''} with location data · Click a pin for details</div>
            {!leafletReady ? <div style={styles.emptyTab}>Loading map...</div> : <div ref={mapRef} style={{ height: '480px', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }} />}
        </div>
    );
}

// ── Visual Artist: Festivals (Festival Browse columns) ───────────────────────
const fmtShort = d => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');
function festivalDates(start, end) {
    const a = fmtShort(start); const b = fmtShort(end);
    if (!a && !b) return '—';
    if (!b || a === b) return a || b;
    return `${a} – ${b}`;
}

function CreditedFestivalsTab({ festivals }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!festivals || festivals.length === 0) return <div style={styles.emptyTab}>No festivals among the credited shows yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? festivals.filter(f => [f.festival_name, f.city, f.state, f.country, festivalDates(f.start_date, f.end_date)].some(v => v && normalizeStr(v).includes(q))) : festivals;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by festival, city, state, country, date..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No festivals match your search.</div> : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Festival</th><th style={styles.th}>City</th><th style={styles.th}>State</th><th style={styles.th}>Country</th><th style={styles.th}>Dates</th></tr></thead>
                            <tbody>
                                {paginated.map((f, idx) => (
                                    <tr key={f.festival_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}><Link to={`/festivals/${f.festival_slug || f.festival_id}`} style={styles.tableLink}>{f.festival_name}</Link></td>
                                        <td style={styles.td}>{f.city || '—'}</td>
                                        <td style={styles.td}>{f.state || '—'}</td>
                                        <td style={styles.td}>{f.country || '—'}</td>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{festivalDates(f.start_date, f.end_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Visual Artist: Flyers / Event Photo Credits galleries ───────────────────
// Same controls as the Flyers tab's Show Flyer History (search-by dropdown +
// text + date sort), over the images credited to this artist.
const CREDIT_SEARCH_OPTIONS = ['Artist', 'Festival', 'Promoter', 'Venue'];
const CREDIT_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
];

function creditItemMatches(item, type, q) {
    const has = v => v && normalizeStr(v).includes(q);
    return item.events.some(e => {
        switch (type) {
            case 'artist':   return has(e.artists) || has(item.photo_artist && item.photo_artist.name);
            case 'festival': return has(e.festival_name);
            case 'promoter': return has(e.promoters);
            case 'venue':    return has(e.venue_name) || has(e.city);
            default:         return true;
        }
    }) || has(item.caption);
}

const itemLatest   = item => (item.events.length ? new Date(item.events[item.events.length - 1].event_date) : 0);
const itemEarliest = item => (item.events.length ? new Date(item.events[0].event_date) : 0);

function creditEventLine(item) {
    const evs = item.events;
    if (evs.length === 0) return '';
    if (evs.length === 1) return [fmtShort(evs[0].event_date), evs[0].venue_name].filter(Boolean).join(' · ');
    return `${evs.length} shows · ${fmtShort(evs[0].event_date)} – ${fmtShort(evs[evs.length - 1].event_date)}`;
}

function CreditCard({ item, kind }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [open, setOpen] = useState(false);
    const line = creditEventLine(item);
    const single = item.events.length === 1 ? item.events[0] : null;
    const overlay = kind === 'photo' && item.photo_artist ? `${item.photo_artist.name} · ${fmtShort(single && single.event_date)}` : line;
    const tooltip = [kind === 'photo' && item.photo_artist ? item.photo_artist.name : null, line, single && single.artists].filter(Boolean).join(' · ');
    return (
        <>
            <div
                style={{ ...FS.flyerCell, ...(kind === 'photo' ? { aspectRatio: '4 / 3' } : {}), cursor: imgError ? 'default' : 'zoom-in' }}
                title={tooltip}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => { if (!imgError) setOpen(true); }}
            >
                {imgError ? (
                    <div style={FS.flyerErrorBox}>
                        <div style={FS.flyerErrorIcon}>{kind === 'photo' ? '📷' : '🎟️'}</div>
                        <div style={FS.flyerErrorDate}>{line}</div>
                    </div>
                ) : (
                    <img src={item.image_url} alt={tooltip} style={{ ...FS.flyerImg, opacity: hovered ? 0.85 : 1 }} loading="lazy" onError={() => setImgError(true)} />
                )}
                {!imgError && (
                    <div style={{ ...FS.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                        <div style={FS.flyerDate}>{overlay}</div>
                    </div>
                )}
                {!imgError && <ZoomHint visible={hovered} />}
            </div>
            {open && (
                <ImageLightbox
                    src={item.image_url}
                    alt={tooltip}
                    caption={[item.caption, line].filter(Boolean).join(' — ')}
                    viewShowTo={single ? `/events/${single.event_slug || single.event_id}` : undefined}
                    onClose={() => setOpen(false)}
                    imageMeta={kind === 'photo' && item.photo_artist ? { photo_artist_name: item.photo_artist.name, photo_artist_slug: item.photo_artist.slug } : undefined}
                />
            )}
        </>
    );
}

function CreditGalleryTab({ items, kind }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState('date_desc');
    const [filterType, setFilterType] = useState('artist');
    const [filterSearch, setFilterSearch] = useState('');

    if (!items || items.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                {kind !== 'photo' && <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎟️</div>}
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>{kind === 'photo' ? 'No Event Photo Credits Yet' : 'No Flyers Yet'}</div>
                <div style={{ color: '#555', fontSize: '14px' }}>{kind === 'photo' ? 'Credited event photos' : 'Credited flyers and posters'} will appear here as they are added.</div>
            </div>
        );
    }

    const q = normalizeStr(filterSearch.trim());
    const filtered = q ? items.filter(it => creditItemMatches(it, filterType, q)) : items;
    const sorted = [...filtered].sort((a, b) => (sortKey === 'date_asc' ? itemEarliest(a) - itemEarliest(b) : itemLatest(b) - itemLatest(a)));
    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const noun = kind === 'photo' ? 'photo' : 'flyer';

    return (
        <div>
            <div style={FS.flyerControlsBar}>
                <div style={FS.flyerFilterGroup}>
                    <select value={filterType} onChange={e => { setFilterType(e.target.value); setFilterSearch(''); setPage(1); }} style={FS.flyerFilterTypeSelect}>
                        {CREDIT_SEARCH_OPTIONS.map(opt => <option key={opt} value={opt.toLowerCase()}>{opt}</option>)}
                    </select>
                    <input type="text" value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }} placeholder={`Search ${filterType}...`} style={FS.flyerFilterInput} />
                    {filterSearch && <button onClick={() => { setFilterSearch(''); setPage(1); }} style={FS.flyerFilterClear} title="Clear filter">✕</button>}
                </div>
                <div style={FS.flyerSortGroup}>
                    <label style={FS.flyerSortLabel}>Sort by</label>
                    <select value={sortKey} onChange={e => { setSortKey(e.target.value); setPage(1); }} style={FS.flyerSortSelect}>
                        {CREDIT_SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>
            </div>

            {sorted.length === 0 ? <div style={styles.emptyTab}>No {noun}s match your filter.</div> : (
                <>
                    <div style={isMobile ? { ...FS.flyerGrid, ...FSmobile.flyerGrid } : FS.flyerGrid}>
                        {paginated.map(it => <CreditCard key={it.image_id} item={it} kind={kind} />)}
                    </div>
                    <Pagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} pageSize={FLYER_PAGE_SIZE} />
                    <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        {q ? `${sorted.length} of ${items.length} ${noun}${items.length !== 1 ? 's' : ''}` : `${items.length} ${noun}${items.length !== 1 ? 's' : ''}`}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Visual Artist: Non-Event Photo Credits gallery (2026-10-15) ─────────────
// Public Non-Event images credited to this Visual Artist that sit on another
// record (artist / festival / member / promoter / venue) — getArtistCredits
// non_event_credits. Same controls as the event galleries: record-type
// dropdown + text search (record name or caption) + upload-date sort.
const NON_EVENT_TYPES = ['artist', 'festival', 'member', 'promoter', 'venue'];
const typeLabel = t => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');
const recordPath = item => `/${item.entity_type}s/${item.entity_slug || item.entity_id}`;
const NON_EVENT_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Uploaded (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Uploaded (Oldest to Most Recent)' },
];

function NonEventCreditCard({ item, creditArtist }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [open, setOpen] = useState(false);
    const record = `${typeLabel(item.entity_type)} · ${item.entity_name}`;
    const tooltip = [record, item.caption].filter(Boolean).join(' · ');
    return (
        <div style={{ minWidth: 0 }}>
            <div
                style={{ ...FS.flyerCell, aspectRatio: '4 / 3', cursor: imgError ? 'default' : 'zoom-in' }}
                title={tooltip}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => { if (!imgError) setOpen(true); }}
            >
                {imgError ? (
                    <div style={FS.flyerErrorBox}>
                        <div style={FS.flyerErrorIcon}>📷</div>
                        <div style={FS.flyerErrorDate}>{record}</div>
                    </div>
                ) : (
                    <img src={item.image_url} alt={tooltip} style={{ ...FS.flyerImg, opacity: hovered ? 0.85 : 1 }} loading="lazy" onError={() => setImgError(true)} />
                )}
                {!imgError && item.caption && (
                    <div style={{ ...FS.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                        <div style={FS.flyerDate}>{item.caption}</div>
                    </div>
                )}
                {!imgError && <ZoomHint visible={hovered} />}
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {typeLabel(item.entity_type)}: <Link to={recordPath(item)} style={{ color: '#bb86fc', textDecoration: 'none' }}>{item.entity_name}</Link>
            </div>
            {open && (
                <ImageLightbox
                    src={item.image_url}
                    alt={tooltip}
                    caption={[item.caption, `${typeLabel(item.entity_type)}: ${item.entity_name}`].filter(Boolean).join(' — ')}
                    viewShowTo={recordPath(item)}
                    viewLinkLabel={`View ${typeLabel(item.entity_type)} →`}
                    onClose={() => setOpen(false)}
                    imageMeta={creditArtist ? { credit_artist_name: creditArtist.name, credit_artist_slug: creditArtist.slug } : undefined}
                />
            )}
        </div>
    );
}

function NonEventCreditGalleryTab({ items, creditArtist }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState('date_desc');
    const [filterType, setFilterType] = useState('');
    const [filterSearch, setFilterSearch] = useState('');

    if (!items || items.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Non-Event Photo Credits Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Credited photos on artist, member, festival, promoter and venue records will appear here as they are added.</div>
            </div>
        );
    }

    const q = normalizeStr(filterSearch.trim());
    const filtered = items.filter(it =>
        (!filterType || it.entity_type === filterType) &&
        (!q || normalizeStr(it.entity_name).includes(q) || normalizeStr(it.caption).includes(q))
    );
    const uploaded = it => new Date(it.created_at || 0).getTime();
    const sorted = [...filtered].sort((a, b) => (sortKey === 'date_asc' ? uploaded(a) - uploaded(b) : uploaded(b) - uploaded(a)) || (b.image_id - a.image_id));
    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const isFiltered = !!(q || filterType);

    return (
        <div>
            <div style={FS.flyerControlsBar}>
                <div style={FS.flyerFilterGroup}>
                    <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} style={FS.flyerFilterTypeSelect}>
                        <option value="">All Records</option>
                        {NON_EVENT_TYPES.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
                    </select>
                    <input type="text" value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }} placeholder="Search name or caption..." style={FS.flyerFilterInput} />
                    {filterSearch && <button onClick={() => { setFilterSearch(''); setPage(1); }} style={FS.flyerFilterClear} title="Clear filter">✕</button>}
                </div>
                <div style={FS.flyerSortGroup}>
                    <label style={FS.flyerSortLabel}>Sort by</label>
                    <select value={sortKey} onChange={e => { setSortKey(e.target.value); setPage(1); }} style={FS.flyerSortSelect}>
                        {NON_EVENT_SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>
            </div>

            {sorted.length === 0 ? <div style={styles.emptyTab}>No photos match your filter.</div> : (
                <>
                    <div style={isMobile ? { ...FS.flyerGrid, ...FSmobile.flyerGrid } : FS.flyerGrid}>
                        {paginated.map(it => <NonEventCreditCard key={it.image_id} item={it} creditArtist={creditArtist} />)}
                    </div>
                    <Pagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} pageSize={FLYER_PAGE_SIZE} />
                    <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        {isFiltered ? `${sorted.length} of ${items.length} photo${items.length !== 1 ? 's' : ''}` : `${items.length} photo${items.length !== 1 ? 's' : ''}`}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Visual Artist: Photos tab (2026-10-15) ───────────────────────────────────
// Three sub-tabs: Event Photo Credits (credits.photos — the former Photos of
// Artists tab), Non-Event Photo Credits (credits.non_event_credits) and
// Non-Event Photos (the artist's own record images — PhotosTab unsplit, with
// Add Photo / Set Default). PhotosTab stays mounted (hidden) so its count is
// known for the sub-tab label without a second request.
function VAPhotosTab({ subTab, onSubTabChange, credits, pendingContent, artist, defaultImageId, onDefaultChanged }) {
    const [nonEventCount, setNonEventCount] = useState(null);
    const withCount = (label, n) => (n === null || n === undefined ? label : `${label} (${n})`);
    const tabs = [
        withCount(VA_PHOTOS_SUBTABS[0], credits ? (credits.photos || []).length : null),
        withCount(VA_PHOTOS_SUBTABS[1], credits ? (credits.non_event_credits || []).length : null),
        withCount(VA_PHOTOS_SUBTABS[2], nonEventCount),
    ];
    return (
        <div>
            <SubTabBar tabs={tabs} active={subTab} onChange={onSubTabChange} />
            <div style={styles.subTabContent}>
                {subTab === 0 && (pendingContent || <CreditGalleryTab items={credits.photos} kind="photo" />)}
                {subTab === 1 && (pendingContent || <NonEventCreditGalleryTab items={credits.non_event_credits || []} creditArtist={artist} />)}
                <div style={{ display: subTab === 2 ? 'block' : 'none' }}>
                    <PhotosTab
                        artistId={artist.id}
                        artistName={artist.name}
                        defaultImageId={defaultImageId}
                        onDefaultChanged={onDefaultChanged}
                        onCount={setNonEventCount}
                    />
                </div>
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function ArtistDetail({ slugOverride } = {}) {
    const isMobile = useIsMobile();
    const params = useParams();
    const navigate = useNavigate();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Section 3 parent tab + sub-tab state
    const [activeParentTab, setActiveParentTab] = useState(0);
    const [activeRelationshipsSubTab, setActiveRelationshipsSubTab] = useState(0);
    const [activeLocationsSubTab, setActiveLocationsSubTab] = useState(0);
    const [activeVAPhotosSubTab, setActiveVAPhotosSubTab] = useState(0);

    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);

    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [caughtMap, setCaughtMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    // Visual Artist credits (GET /artists/{id}/credits)
    const [credits, setCredits] = useState(null);
    const [creditsError, setCreditsError] = useState(null);

    const loadAttendedStatus = useCallback(async (shows, artistId) => {
        if (!shows || shows.length === 0) return;
        try {
            const eventIds = shows.map(s => s.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds, artistId);
            const aMap = {}; const pMap = {}; const wMap = {}; const cMap = {};
            Object.entries(statusMap).forEach(([id, status]) => { aMap[id] = status.attended; pMap[id] = status.performed || false; wMap[id] = status.worked || false; cMap[id] = status.caught || false; });
            setAttendedMap(aMap); setPerformedMap(pMap); setWorkedMap(wMap); setCaughtMap(cMap);
        } catch { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) { console.error('toggleAttended error:', e); }
        finally { setTogglingMap(prev => ({ ...prev, [eventId]: false })); }
    }, []);

    useEffect(() => {
        async function load() {
            setDefaultImageId(null);
            setDefaultImageUrl(null);
            setDefaultImageResolving(false);
            setCredits(null);
            setCreditsError(null);
            setActiveVAPhotosSubTab(0);
            try {
                const res = await axios.get(`${config.API_URL}/artists/${slug}`);
                setData(res.data);
                setLoading(false);
                if (res.data?.artist) document.title = `SHARC - ${res.data.artist.name}`;

                const art = res.data?.artist;
                // Visual Artist (2026-10-19): start the credits request right
                // away so it runs alongside the default-image lookup below
                // instead of after it. Settled to {data}/{error} so it can't
                // surface as an unhandled rejection before it's awaited.
                const creditsPromise = art?.artist_type === VISUAL_ARTIST_TYPE
                    ? axios.get(`${config.API_URL}/artists/${art.id}/credits`).then(r => ({ data: r.data }), error => ({ error }))
                    : null;
                if (art?.default_image_id) {
                    setDefaultImageId(art.default_image_id);
                    if (art.default_image_url) {
                        setDefaultImageUrl(art.default_image_url);
                    } else {
                        // Lambda didn't include a resolved URL — fetch it
                        // eagerly rather than waiting for the Photos tab to
                        // mount. Without this, an artist with a default set
                        // but no legacy image_url shows no hero image at all
                        // until the user happens to open Photos. Mirrors the
                        // same fetch EventDetail.js/FestivalDetail.js do.
                        setDefaultImageResolving(true);
                        try {
                            const token = await getIdToken();
                            const imgRes = await axios.get(`${config.API_URL}/images`, {
                                params: { entityType: 'artist', entityId: art.id },
                                headers: { Authorization: token },
                            });
                            const defaultImg = (imgRes.data.images || []).find(i => i.id === art.default_image_id);
                            if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                        } catch { /* fall back gracefully to legacy image_url */ }
                        finally { setDefaultImageResolving(false); }
                    }
                }
                if (res.data?.show_history) loadAttendedStatus(res.data.show_history, art?.id);

                // Visual Artist: every tab is driven by credited images
                if (creditsPromise) {
                    const cr = await creditsPromise;
                    if (cr.data) {
                        setCredits(cr.data);
                        if (cr.data.show_history?.length) loadAttendedStatus(cr.data.show_history, art.id);
                    } else {
                        setCreditsError('Failed to load credits');
                    }
                }
            } catch {
                setError('Failed to load artist');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    const handleDefaultChanged = useCallback((imageId, imageUrl) => {
        setDefaultImageId(imageId);
        setDefaultImageUrl(imageUrl);
    }, []);

    if (loading) return <div style={styles.message}>Loading artist...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { artist, stats, played_with, promoter_relationships, top_venues, geography, show_history, show_map, formerly_known_as, hometowns, links, projects, has_existing_events, pending_deletion } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const formatRequestedAt = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const heroImage = defaultImageUrl || artist.image_url;

    // Visual Artist variant (2026-10-12)
    const isVA = artist.artist_type === VISUAL_ARTIST_TYPE;
    const infoTabs = isVA ? VA_INFO_TABS : INFO_TABS;
    const parentTabs = isVA ? VA_PARENT_TABS : PARENT_TABS;
    const locationsSubtabs = isVA ? VA_LOCATIONS_SUBTABS : LOCATIONS_SUBTABS;
    const infoTab = infoTabs[activeInfoTab] || infoTabs[0];
    const parentTab = parentTabs[activeParentTab] || parentTabs[0];
    const locationsSubtab = locationsSubtabs[activeLocationsSubTab] || locationsSubtabs[0];
    // (2026-10-22) Matched by name, like Locations — a Visual Artist has two
    // extra sub-tabs, and the index survives client-side navigation.
    const relationshipsSubtabs = isVA ? VA_RELATIONSHIPS_SUBTABS : RELATIONSHIPS_SUBTABS;
    const relationshipsSubtab = relationshipsSubtabs[activeRelationshipsSubTab] || relationshipsSubtabs[0];
    const vaPending = isVA && !credits; // still loading, or failed
    const vaPlaceholder = <div style={styles.emptyTab}>{creditsError || 'Loading credits…'}</div>;
    const statSource = isVA ? (credits?.stats || {}) : stats;

    // Seen = Caught, Performed, or Worked on any show in this artist's history
    // (Artist "Seen" Status, 2026-09-06). Plain Attended isn't checked directly —
    // toggleAttended.js auto-marks every artist on an attended event as caught,
    // so Attended is already folded into the caught check.
    // Event photo link text on the Photos tab: "<Month> <Year>: <City>, <State>"
    const showById = {};
    (show_history || []).forEach(s => { showById[s.event_id] = s; });
    const eventSummary = (eventId) => {
        const s = showById[eventId];
        if (!s || !s.event_date) return null;
        const when = new Date(s.event_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
        const where = [s.city, s.state || s.country].filter(Boolean).join(', ');
        return where ? `${when}: ${where}` : when;
    };

    const hasSeen = (show_history || []).some(s => caughtMap[s.event_id] || performedMap[s.event_id] || workedMap[s.event_id]);

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            {/* ── Section 1: Artist Name + Seen badge + Actions dropdown ── */}
            <div style={styles.section1}>
                <h1 style={styles.artistNameHeading}>{artist.name}</h1>
                <div style={styles.section1Right}>
                    {hasSeen && !isVA && (
                        <span style={styles.seenBadge}>✓ Seen</span>
                    )}
                    {!pending_deletion && (
                        <ActionsDropdown
                            editPath={`/artists/${artist.slug || artist.id}/edit`}
                            editLabel="Edit Artist"
                            onDeleteClick={() => setShowDeleteModal(true)}
                            deleteLabel="Delete Artist"
                            deleteDisabled={has_existing_events}
                            deleteTooltip="This artist cannot be deleted while it is tied to an existing event. Remove it from all events first."
                        />
                    )}
                </div>
            </div>

            {pending_deletion && (
                <div style={styles.pendingDeletionBanner}>
                    <span style={styles.pendingDeletionIcon}>🗑️</span>
                    <span>
                        Artist marked for deletion by <strong>{pending_deletion.requested_by}</strong> on{' '}
                        <strong>{formatRequestedAt(pending_deletion.requested_at)}</strong>
                        {pending_deletion.reason && (
                            <> — <em>{pending_deletion.reason}</em></>
                        )}
                    </span>
                </div>
            )}

            {showDeleteModal && (
                <DeletionRequestModal
                    entityType="artist"
                    entityId={artist.id}
                    entityName={artist.name}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => navigate('/artists')}
                />
            )}

            {/* ── Section 2: Image/meta left + info tabs right ── */}
            <div style={isMobile ? { ...styles.headerGrid, ...Smobile.headerGrid } : styles.headerGrid}>
                {/* Left: image + location + type/genre */}
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {!defaultImageResolving && heroImage ? (
                            <img
                                src={heroImage}
                                alt={artist.name}
                                style={styles.artistImage}
                                onError={e => { e.target.outerHTML = `<div style="width:160px;height:160px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`; }}
                            />
                        ) : (
                            <div style={styles.noImageBox}>
                                <div style={styles.noImageLabel}>{defaultImageResolving ? '…' : 'No Image'}</div>
                            </div>
                        )}
                        <div style={styles.headerMeta}>
                            {[artist.city, artist.state, artist.country].filter(Boolean).length > 0 && (
                                <div style={styles.location}>{[artist.city, artist.state, artist.country].filter(Boolean).join(', ')}</div>
                            )}
                            {artist.sharc_id ? (
                                <span style={styles.verifiedBadge}>✔ Verified</span>
                            ) : (
                                <span style={styles.unverifiedBadge}>Unverified</span>
                            )}
                            {artist.artist_type && (
                                <span style={styles.tag}>{artist.artist_type}</span>
                            )}
                            {artist.artist_style && (
                                <span style={{ ...styles.tag, marginTop: '4px' }}>{artist.artist_style}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: info tabs */}
                <div style={styles.headerRight}>
                    {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                    {isMobile ? (
                        <MobileTabSelect tabs={infoTabs} active={infoTabs.indexOf(infoTab)} onChange={setActiveInfoTab} />
                    ) : (
                        <div style={styles.infoTabBar}>
                            {infoTabs.map((tab, i) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveInfoTab(i)}
                                    style={{ ...styles.infoTabBtn, ...(infoTab === tab ? styles.infoTabBtnActive : {}) }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={styles.infoTabContent}>
                        {/* Show Stats */}
                        {infoTab === 'Show Stats' && (() => {
                            const stats = statSource;
                            // Visual Artist (2026-10-19): until credits arrive show
                            // "…" (or "—" if they failed), never a misleading 0
                            const statsPending = isVA && !credits;
                            const pendingMark = creditsError ? '—' : '…';
                            const countOf = v => (statsPending ? pendingMark : (v || 0));
                            const dateOf = d => (statsPending ? pendingMark : formatDate(d));
                            let termStr = statsPending ? pendingMark : '—';
                            if (stats.first_show_date && stats.last_show_date) {
                                let d1 = new Date(stats.first_show_date); let d2 = new Date(stats.last_show_date);
                                let years = d2.getUTCFullYear() - d1.getUTCFullYear(); let months = d2.getUTCMonth() - d1.getUTCMonth(); let days = d2.getUTCDate() - d1.getUTCDate();
                                if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
                                if (months < 0) { years -= 1; months += 12; }
                                const parts = [];
                                if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
                                if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
                                if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
                                termStr = parts.join(' ') || '—';
                            }
                            return (
                                <div style={isMobile ? { ...styles.showStatsLayout, ...Smobile.showStatsLayout } : styles.showStatsLayout}>
                                    {isVA ? (
                                        // Visual Artist: Total Shows + Total # of Images, side by
                                        // side on mobile so the dates stay inside the panel
                                        <div style={isMobile ? { ...styles.vaStatBoxes, ...Smobile.vaStatBoxes } : styles.vaStatBoxes}>
                                            {[[stats.total_shows, 'Total Shows'], [stats.total_images, 'Total # of Images']].map(([v, label]) => (
                                                <div key={label} style={isMobile ? { ...styles.totalShowsBox, ...Smobile.vaStatBox } : styles.totalShowsBox}>
                                                    <div style={isMobile ? { ...styles.statValue, ...Smobile.vaStatValue } : styles.statValue}>{countOf(v)}</div>
                                                    <div style={styles.statLabel}>{label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={styles.totalShowsBox}>
                                            <div style={styles.statValue}>{stats.total_shows || 0}</div>
                                            <div style={styles.statLabel}>Total Shows</div>
                                        </div>
                                    )}
                                    <table style={styles.statsTable}>
                                        <tbody>
                                            <tr>
                                                <td style={styles.statsTableLabel}>First Show</td>
                                                <td style={styles.statsTableValue}>{dateOf(stats.first_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Most Recent Show</td>
                                                <td style={styles.statsTableValue}>{dateOf(stats.last_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Term of Shows</td>
                                                <td style={styles.statsTableValue}>{termStr}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}

                        {/* Bio */}
                        {infoTab === 'Bio' && (
                            <div>
                                {artist.description
                                    ? <div style={styles.bioText}>{artist.description}</div>
                                    : <div style={styles.emptyInfo}>No bio available.</div>}
                            </div>
                        )}

                        {/* Links */}
                        {infoTab === 'Links' && (
                            <div>
                                {links && links.length > 0
                                    ? <div style={styles.linksGrid}>
                                        {links.map(link => (
                                            <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.linkChip}>
                                                {link.description} ↗
                                            </a>
                                        ))}
                                    </div>
                                    : <div style={styles.emptyInfo}>No links added yet.</div>}
                            </div>
                        )}

                        {/* Projects */}
                        {infoTab === 'Projects' && (
                            <ProjectsTab projects={projects} entityType="artist" />
                        )}

                        {/* Additional Info */}
                        {infoTab === 'Additional Info' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {hometowns && hometowns.length > 0 && (
                                    <div>
                                        <div style={styles.infoLabel}>Hometown</div>
                                        <div style={styles.infoValue}>
                                            {hometowns.map(ht => [ht.city, ht.state, ht.country].filter(Boolean).join(', ')).join(' · ')}
                                        </div>
                                    </div>
                                )}
                                {formerly_known_as && formerly_known_as.length > 0 && (
                                    <div>
                                        <div style={styles.infoLabel}>Formerly Known As</div>
                                        <div style={styles.infoValue}>{formerly_known_as.map(f => f.name).join(', ')}</div>
                                    </div>
                                )}
                                {(!hometowns || hometowns.length === 0) && (!formerly_known_as || formerly_known_as.length === 0) && (
                                    <div style={styles.emptyInfo}>No additional information available.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Multi-level tabs ── */}
            <div style={styles.tabsWrapper}>
                {/* Parent tab bar — mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                {isMobile ? (
                    <MobileTabSelect tabs={parentTabs} active={parentTabs.indexOf(parentTab)} onChange={setActiveParentTab} />
                ) : (
                    <div style={styles.tabBar}>
                        {parentTabs.map((tab, i) => (
                            <button key={tab} onClick={() => setActiveParentTab(i)} style={{ ...styles.tabBtn, ...(parentTab === tab ? styles.tabBtnActive : {}) }}>{tab}</button>
                        ))}
                    </div>
                )}

                <div style={styles.tabContent}>
                    {/* ── Show Archive ── */}
                    {parentTab === 'Show Archive' && (vaPending ? vaPlaceholder : (
                        <ShowHistoryTab
                            shows={isVA ? credits.show_history : show_history}
                            attendedMap={attendedMap}
                            performedMap={performedMap}
                            workedMap={workedMap}
                            onToggleAttended={handleToggleAttended}
                            togglingMap={togglingMap}
                            creditColumn={isVA}
                            emptyText={isVA ? 'No credited shows yet.' : undefined}
                        />
                    ))}

                    {/* ── Shows by Year ── */}
                    {parentTab === 'Shows by Year' && (vaPending ? vaPlaceholder : <ShowsByYearTab shows={isVA ? credits.show_history : show_history} />)}

                    {/* ── Members (not on Visual Artists) ── */}
                    {parentTab === 'Members' && <ArtistMembersTab artistId={artist.id} />}

                    {/* ── Relationships ── */}
                    {parentTab === 'Relationships' && (vaPending ? vaPlaceholder : (
                        <div>
                            <SubTabBar tabs={relationshipsSubtabs} active={relationshipsSubtabs.indexOf(relationshipsSubtab)} onChange={setActiveRelationshipsSubTab} />
                            <div style={styles.subTabContent}>
                                {relationshipsSubtab === 'Artist Relationships' && <ArtistRelationshipsTab playedWith={isVA ? credits.artist_relationships : played_with} showFlyerCount={isVA} />}
                                {relationshipsSubtab === 'Promoter Relationships' && <PromoterRelationshipsTab promoterRelationships={isVA ? credits.promoter_relationships : promoter_relationships} showFlyerCount={isVA} />}
                                {relationshipsSubtab === 'Festival Relationships' && <PrimaryRelationshipsTab rows={credits.festival_relationships} entityLabel="Festival" pathPrefix="/festivals" />}
                                {relationshipsSubtab === 'Venue Relationships' && <PrimaryRelationshipsTab rows={credits.venue_relationships} entityLabel="Venue" pathPrefix="/venues" />}
                            </div>
                        </div>
                    ))}

                    {/* ── Locations ── */}
                    {parentTab === 'Locations' && (vaPending ? vaPlaceholder : (
                        <div>
                            <SubTabBar tabs={locationsSubtabs} active={locationsSubtabs.indexOf(locationsSubtab)} onChange={setActiveLocationsSubTab} />
                            <div style={styles.subTabContent}>
                                {locationsSubtab === 'Shows Map' && <GeographicMapTab showMap={isVA ? credits.show_map : show_map} />}
                                {locationsSubtab === 'Geographic Breakdown' && <GeographicBreakdownTab geography={isVA ? credits.geography : geography} />}
                                {locationsSubtab === 'Festivals Played' && <FestivalsPlayedTab shows={show_history} />}
                                {locationsSubtab === 'Venues Played' && <VenuesPlayedTab venues={top_venues} />}
                                {locationsSubtab === 'Festivals' && <CreditedFestivalsTab festivals={credits.festivals} />}
                                {locationsSubtab === 'Venues' && <VenuesPlayedTab venues={credits.venues} />}
                            </div>
                        </div>
                    ))}

                    {/* ── Flyers ── */}
                    {parentTab === 'Flyers' && isVA && (vaPending ? vaPlaceholder : <CreditGalleryTab items={credits.flyers} kind="flyer" />)}
                    {parentTab === 'Flyers' && !isVA && (
                        <FlyerHistoryTabs
                            entityType="artist"
                            entityId={artist.id}
                            shows={show_history}
                            searchByOptions={['Artist', 'Festival', 'Promoter', 'Venue']}
                            multiEventTabLabel="Tour/Schedule Flyer History"
                            showFlyerHistoryLabel="Show Flyer History"
                        />
                    )}

                    {/* ── Photos (bottom folder for every artist type) ──
                        Non-visual: Event Photos / Non-Event Photos sub-tabs (2026-10-12).
                        Visual Artist: Event Photo Credits / Non-Event Photo Credits /
                        Non-Event Photos sub-tabs (2026-10-15). */}
                    {parentTab === 'Photos' && !isVA && (
                        <PhotosTab
                            artistId={artist.id}
                            artistName={artist.name}
                            defaultImageId={defaultImageId}
                            onDefaultChanged={handleDefaultChanged}
                            split
                            eventSummary={eventSummary}
                        />
                    )}
                    {parentTab === 'Photos' && isVA && (
                        <VAPhotosTab
                            subTab={activeVAPhotosSubTab}
                            onSubTabChange={setActiveVAPhotosSubTab}
                            credits={credits}
                            pendingContent={vaPending ? vaPlaceholder : null}
                            artist={artist}
                            defaultImageId={defaultImageId}
                            onDefaultChanged={handleDefaultChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    // ── Section 1 ──
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    section1Right: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
    artistNameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    // Static status badge — not a button. No cursor/hover affordance. Sized
    // to match the top-level Actions button / Performed-Worked-Attended badge
    // treatment used on EventDetail.js's top action bar (7px 16px padding,
    // 14px font, 6px radius) rather than the smaller per-artist lineup buttons.
    seenBadge: { display: 'inline-block', backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'default' },
    pendingDeletionBanner: {
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        backgroundColor: '#2c1818', border: '1px solid #e57373', borderRadius: '6px',
        padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#e0a0a0', lineHeight: 1.5
    },
    pendingDeletionIcon: { fontSize: '16px', lineHeight: 1.4, flexShrink: 0 },

    // ── Section 2 header grid ──
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    artistImage: { width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    noImageBox: { width: '160px', height: '160px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px' },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    tag: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    verifiedBadge: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#4caf50', marginBottom: '6px' },
    unverifiedBadge: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#e53935', marginBottom: '6px' },

    // ── Info tabs (right side of section 2) ──
    // minWidth: 0 is the actual fix here — without it, this grid column
    // refuses to shrink below infoTabBar's content width (6 tabs), forcing
    // the whole page wider than the viewport on mobile instead of letting
    // infoTabBar's own overflowX: 'auto' scroll internally as intended.
    headerRight: { minWidth: 0 },
    // Tab bars (2026-09-23 scroll fix, same as MyShows.js): overflowX: 'auto'
    // forces overflow-y to 'auto' too, and the old negative-marginBottom tab
    // buttons overflowed the bar by 1–2px, so a mouse wheel over the bar
    // scrolled the bar itself instead of the page. Divider is now an inset
    // box-shadow (painted beneath the buttons, so the active underline covers
    // it with zero overflow) plus an explicit overflowY: 'hidden'.
    infoTabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', gap: '2px', overflowX: 'auto', overflowY: 'hidden' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto' },
    bioText: { fontSize: '14px', color: '#ccc', lineHeight: '1.7', maxHeight: '96px', overflowY: 'auto' },

    // ── Show Stats tab ──
    showStatsLayout: { display: 'flex', gap: '24px', alignItems: 'center', height: '100%' },
    vaStatBoxes: { display: 'flex', gap: '24px', flexShrink: 0 },
    totalShowsBox: { flexShrink: 0, backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 28px', textAlign: 'center', minWidth: '110px' },
    statsTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },
    linksGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    linkChip: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    emptyInfo: { color: '#555', fontSize: '14px', padding: '20px 0', textAlign: 'center' },
    infoLabel: { fontSize: '11px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    infoValue: { fontSize: '14px', color: '#ccc' },

    // ── Photos tab ──
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },

    // ── Stat value/label ──
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // ── Section 3: Parent tabs ──
    tabsWrapper: { marginTop: '8px', width: '100%', minWidth: 0 },
    tabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px', width: '100%', minWidth: 0, boxSizing: 'border-box' },

    // ── Sub-tabs (inside parent tab content) ──
    subTabBar: { display: 'flex', boxShadow: 'inset 0 -1px 0 #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },

    // ── Tables ──
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    // Jump-to-page pagination — copied from PromoterDetail.js
    pageJumpWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // ── Flyer History grid ──
    flyerGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '10px',
        marginBottom: '8px',
    },
    flyerCell: {
        position: 'relative',
        display: 'block',
        aspectRatio: '8.5 / 11',
        overflow: 'hidden',
        borderRadius: '4px',
        border: '1px solid #2e2e2e',
        backgroundColor: '#111',
        textDecoration: 'none',
        cursor: 'pointer',
    },
    flyerErrorBox: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#161616',
        border: '1px dashed #2e2e2e',
        borderRadius: '4px',
        padding: '12px',
    },
    flyerErrorIcon: {
        fontSize: '28px',
        opacity: 0.25,
    },
    flyerErrorDate: {
        fontSize: '10px',
        color: '#555',
        fontWeight: '600',
        textAlign: 'center',
        lineHeight: 1.4,
    },
    flyerErrorVenue: {
        fontSize: '10px',
        color: '#444',
        textAlign: 'center',
        lineHeight: 1.4,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    flyerCellPad: {
        aspectRatio: '8.5 / 11',
        visibility: 'hidden',
    },
    flyerImg: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        transition: 'opacity 0.15s ease',
    },
    flyerOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)',
        padding: '20px 6px 5px',
        opacity: 0,
        transition: 'opacity 0.15s ease',
    },
    flyerDate: {
        fontSize: '10px',
        color: '#e0e0e0',
        fontWeight: '600',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    flyerPagination: {
        display: 'flex',
        justifyContent: 'center',
        gap: '6px',
        marginTop: '20px',
        flexWrap: 'wrap',
    },

    // ── Geographic Breakdown (collapsible) ──
    geoGroup: { marginBottom: '4px' },
    geoCountryRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#222', borderRadius: '6px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoCountryName: { flex: 1, fontSize: '14px', fontWeight: 'bold', color: '#e0e0e0' },
    geoStateBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoStateRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#1e1e1e', borderRadius: '5px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoStateName: { flex: 1, fontSize: '13px', fontWeight: '600', color: '#ccc' },
    geoCityBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoCityRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', backgroundColor: '#1a1a1a', borderRadius: '4px', cursor: 'pointer', userSelect: 'none', marginBottom: '1px' },
    geoCityName: { flex: 1, fontSize: '13px', color: '#bbb' },
    geoVenueList: { marginLeft: '16px', paddingBottom: '4px' },
    geoVenueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #222' },
    geoVenueName: { fontSize: '13px', color: '#aaa', flex: 1 },
    geoChevron: { fontSize: '12px', color: '#555', width: '12px', flexShrink: 0 },
    geoCount: { fontSize: '12px', color: '#555', whiteSpace: 'nowrap' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.1) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
//
// NOTE: this file's local `flyerGrid`/`flyerCell`/`flyerCellPad` styles are
// unused dead code — this page renders flyers via the shared
// <FlyerHistoryTabs> component (imported above), not via these local
// styles. No mobile override is needed here; see FlyerHistoryTabs.js instead.
const Smobile = {
    // Image/meta (left) + info tabs (right) — stack instead of a fixed
    // 1fr/2fr grid.
    headerGrid: {
        gridTemplateColumns: '1fr',
        gap: '20px',
    },
    // "Total Shows" box + stats table (in the Show Stats tab) — stack
    // instead of a flex row, so the stats table isn't squeezed into ~200px.
    showStatsLayout: {
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'auto',
    },
    // Visual Artist stat boxes (2026-10-12): two compact boxes in one row
    vaStatBoxes: { gap: '12px' },
    vaStatBox: { flex: 1, minWidth: 0, padding: '12px 8px' },
    vaStatValue: { fontSize: '28px', marginBottom: '4px' },
};

export default ArtistDetail;
